import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    Briefcase,
    MapPin,
    Clock,
    Save,
    X,
    AlertCircle,
    CheckCircle,
    Loader2
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { careersAPI } from '../../services/api';

const CareersManagementPage = () => {
    const [careers, setCareers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingCareer, setEditingCareer] = useState(null);
    const [saving, setSaving] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [permError, setPermError] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        location: '',
        type: 'Full-time',
        description: '',
        requirements: '',
        is_active: true
    });

    const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Internship'];

    // Subscribe to real-time updates
    useEffect(() => {
        setLoading(true);
        const unsubscribe = careersAPI.subscribeToCareers(
            (data) => {
                setCareers(data || []);
                setLoading(false);
            }
        );

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);

    // Filter careers
    const filteredCareers = careers.filter(career =>
        career.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        career.location.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Show notification
    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 4000);
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: '',
            location: '',
            type: 'Full-time',
            description: '',
            requirements: '',
            is_active: true
        });
        setEditingCareer(null);
    };

    // Open add modal
    const handleAdd = () => {
        resetForm();
        setShowModal(true);
    };

    // Open edit modal
    const handleEdit = (career) => {
        setEditingCareer(career);
        setFormData({
            title: career.title || '',
            location: career.location || '',
            type: career.type || 'Full-time',
            description: career.description || '',
            requirements: career.requirements || '',
            is_active: career.is_active !== undefined ? career.is_active : true
        });
        setShowModal(true);
    };

    // Handle save
    const handleSave = async () => {
        if (!formData.title.trim()) {
            showNotification('Job title is required', 'error');
            return;
        }
        if (!formData.location.trim()) {
            showNotification('Location is required', 'error');
            return;
        }

        setSaving(true);
        try {
            let response;
            if (editingCareer?.id) {
                response = await careersAPI.update(editingCareer.id, formData);
                if (response.ok) {
                    showNotification('Career post updated successfully!');
                    setShowModal(false);
                    resetForm();
                } else {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Failed to update career post');
                }
            } else {
                response = await careersAPI.create(formData);
                if (response.ok) {
                    showNotification('Career post created successfully!');
                    setShowModal(false);
                    resetForm();
                } else {
                    const errorData = await response.json();
                    const error = new Error(errorData.detail || 'Failed to create career post');
                    error.code = errorData.code;
                    throw error;
                }
            }
        } catch (error) {
            console.error('Error saving career:', error);
            if (error.message?.toLowerCase().includes('permission') || error.code === 'permission-denied' || error.status === 403) {
                setPermError('save');
            } else {
                showNotification(error.message || 'Failed to save career post', 'error');
            }
        } finally {
            setSaving(false);
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            const response = await careersAPI.delete(id);
            if (response.ok) {
                showNotification('Career post deleted successfully!');
            } else {
                const errorData = await response.json();
                if (errorData.code === 'permission-denied' || errorData.detail?.toLowerCase().includes('permission')) {
                    setPermError('delete');
                } else {
                    throw new Error(errorData.detail || 'Failed to delete career post');
                }
            }
        } catch (error) {
            console.error('Error deleting career:', error);
            showNotification(error.message || 'Failed to delete career post', 'error');
        }
        setDeleteConfirm(null);
    };

    return (
        <AdminLayout title="Career Management">
            <Helmet>
                <title>Manage Careers - Admin Dashboard</title>
            </Helmet>

            {/* Notification */}
            {notification && (
                <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-slide-in ${notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'
                    } text-white`}>
                    {notification.type === 'error' ? (
                        <AlertCircle className="w-5 h-5" />
                    ) : (
                        <CheckCircle className="w-5 h-5" />
                    )}
                    <span className="font-medium">{notification.message}</span>
                </div>
            )}

            {/* Action Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search postings..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50"
                    />
                </div>
                <button
                    onClick={handleAdd}
                    className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-teal-400 hover:to-emerald-400 transition-all shadow-lg shadow-teal-500/25 flex items-center gap-2"
                >
                    <Plus className="w-5 h-5" />
                    Add Posting
                </button>
            </div>

            {/* Table Card */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-gray-400">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
                        Loading postings...
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-white/5 text-gray-400 font-medium text-sm">
                                <tr>
                                    <th className="px-6 py-4">Job Title</th>
                                    <th className="px-6 py-4">Location</th>
                                    <th className="px-6 py-4">Type</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {filteredCareers.length > 0 ? (
                                    filteredCareers.map((career) => (
                                        <tr key={career.id} className="hover:bg-white/5 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center text-white">
                                                        <Briefcase className="w-5 h-5" />
                                                    </div>
                                                    <span className="font-semibold text-white">{career.title}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-gray-300">
                                                <div className="flex items-center gap-2">
                                                    <MapPin className="w-4 h-4 text-gray-500" />
                                                    {career.location}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-gray-300">
                                                    {career.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {career.is_active ? (
                                                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-[10px] uppercase font-bold rounded-full border border-emerald-500/20">Active</span>
                                                ) : (
                                                    <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-[10px] uppercase font-bold rounded-full border border-gray-500/20">Inactive</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end gap-1">
                                                    <button
                                                        onClick={() => handleEdit(career)}
                                                        className="p-2 text-gray-400 hover:text-teal-400 hover:bg-teal-500/10 rounded-lg transition-colors"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(career)}
                                                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                            {searchQuery ? 'No results found matching your search' : 'No career postings yet'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-slate-900 z-10">
                            <h2 className="text-xl font-bold text-white">
                                {editingCareer ? 'Edit Career Post' : 'Add New Career Post'}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white transition-colors">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1.5">Job Title *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                                    placeholder="e.g., Senior Software Engineer"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Location *</label>
                                    <input
                                        type="text"
                                        value={formData.location}
                                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                                        placeholder="e.g., Colombo / Hybrid"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Job Type</label>
                                    <select
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                        className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                                    >
                                        {jobTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1.5">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50 min-h-[100px]"
                                    placeholder="Job description and roles..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1.5">Requirements</label>
                                <textarea
                                    value={formData.requirements}
                                    onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50 min-h-[100px]"
                                    placeholder="Key requirements and qualifications..."
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                />
                                <label htmlFor="is_active" className="text-sm font-medium text-gray-300">
                                    Display post on website
                                </label>
                            </div>
                        </div>

                        <div className="p-6 border-t border-white/10 flex justify-end gap-3 sticky bottom-0 bg-slate-900">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-5 py-2.5 text-gray-400 hover:text-white font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-8 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-bold hover:scale-105 transition-all shadow-lg shadow-teal-500/25 disabled:opacity-50 disabled:scale-100 flex items-center gap-2"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                {editingCareer ? 'Update Post' : 'Create Post'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm">
                        <div className="w-12 h-12 bg-red-500/20 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Trash2 className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-white text-center mb-2">Delete Career Post?</h3>
                        <p className="text-gray-400 text-center mb-6">
                            Are you sure you want to delete <span className="text-white font-medium">{deleteConfirm.title}</span>? This action cannot be undone.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="flex-1 px-4 py-2.5 bg-white/5 text-gray-300 rounded-xl hover:bg-white/10 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(deleteConfirm.id)}
                                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-colors shadow-lg shadow-red-500/25"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Firestore Permission Guide */}
            {permError && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[120] flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl p-8 max-w-lg shadow-2xl border-4 border-orange-500">
                        <div className="flex items-center gap-3 text-orange-500 mb-4">
                            <AlertCircle className="w-8 h-8" />
                            <h3 className="text-2xl font-black uppercase">Database Locked</h3>
                        </div>
                        <p className="text-gray-600 mb-6 font-medium leading-relaxed">
                            Your **Firestore Database** is refusing to {permError === 'delete' ? 'delete' : 'save'} the career post. This usually happens when security rules are not correctly set. 
                            Please paste the rules from **FIREBASE_RULES_SETUP.md** into your **Firebase Console {'>'} Firestore Database {'>'} Rules** tab.
                        </p>
                        <div className="bg-gray-900 text-orange-400 p-4 rounded-xl text-xs overflow-auto mb-6 border border-white/10 shadow-inner max-h-40">
                            <pre>{`match /careers/{careerId} {
  allow read: if true;
  allow write: if request.auth != null;
}`}</pre>
                        </div>
                        <button
                            onClick={() => setPermError(null)}
                            className="w-full bg-orange-500 text-white py-4 rounded-xl font-black uppercase tracking-widest hover:bg-orange-600 transition-all shadow-lg shadow-orange-500/25"
                        >
                            Got it, I'll update them
                        </button>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default CareersManagementPage;
